/**
 * A small Markdown parser for the help-center articles (#2685).
 *
 * Deliberately not a general Markdown implementation: it covers the subset the
 * articles use, as fixed by `docs/help-center/README.md` — headings with an
 * optional stable anchor (`## Titel {#anker}`), paragraphs, bold / italic /
 * inline code / links, ordered and unordered lists (nested by indentation,
 * with continuation lines and blank lines between items), tables, blockquotes,
 * fenced code blocks (used for the two ASCII diagrams) and horizontal rules.
 *
 * It returns a tree that `MarkdownView` renders as React elements, so article
 * text never reaches the DOM as HTML — raw HTML in an article is shown as text.
 * `markdown.test.ts` pins every construct the articles use, and
 * `helpCenterContent.test.ts` parses every shipped article.
 *
 * Line classification uses linear checks rather than backtracking patterns:
 * each construct is recognised by a prefix and then read with plain string
 * operations.
 */

export type Inline =
    | { type: 'text', text: string }
    | { type: 'code', text: string }
    | { type: 'strong', children: Inline[] }
    | { type: 'em', children: Inline[] }
    | { type: 'link', href: string, children: Inline[] };

export interface ListItem {
    blocks: Block[];
}

export type Block =
    | { type: 'heading', level: number, id: string, children: Inline[], text: string }
    | { type: 'paragraph', children: Inline[] }
    | { type: 'list', ordered: boolean, start: number, items: ListItem[] }
    | { type: 'table', header: Inline[][], rows: Inline[][][] }
    | { type: 'blockquote', blocks: Block[] }
    | { type: 'code', text: string }
    | { type: 'hr' };

// ── Line classification ─────────────────────────────────────────────────

const indentOf = (line: string): number => line.length - line.trimStart().length;
const isBlank = (line: string): boolean => line.trim() === '';

interface HeadingLine {
    level: number;
    rest: string;
}

const readHeadingLine = (line: string): HeadingLine | null => {
    let level = 0;
    while (level < line.length && line[level] === '#') level += 1;
    if (level === 0 || level > 6 || line[level] !== ' ') return null;
    return { level, rest: line.slice(level + 1).trim() };
};

/** `Titel {#anker}` → text and explicit anchor. */
const splitAnchor = (rest: string): { text: string, anchor?: string } => {
    const open = rest.lastIndexOf('{#');
    if (open < 0 || !rest.endsWith('}')) return { text: rest };
    const anchor = rest.slice(open + 2, -1);
    return /^[\w-]+$/.test(anchor) ? { text: rest.slice(0, open).trim(), anchor } : { text: rest };
};

interface ListLine {
    indent: number;
    ordered: boolean;
    start: number;
    content: string;
    /** Column where the item's text begins — continuation lines align to it. */
    contentColumn: number;
}

const readListLine = (line: string): ListLine | null => {
    const indent = indentOf(line);
    const body = line.slice(indent);
    if ((body.startsWith('- ') || body.startsWith('* ')) && body.trim().length > 1) {
        return { indent, ordered: false, start: 1, content: body.slice(2).trim(), contentColumn: indent + 2 };
    }
    let digits = 0;
    while (digits < body.length && body[digits] >= '0' && body[digits] <= '9') digits += 1;
    if (digits > 0 && digits <= 3 && body[digits] === '.' && body[digits + 1] === ' ') {
        return {
            indent,
            ordered: true,
            start: parseInt(body.slice(0, digits), 10),
            content: body.slice(digits + 2).trim(),
            contentColumn: indent + digits + 2,
        };
    }
    return null;
};

const isFence = (line: string): boolean => line.trimStart().startsWith('```');
const isQuote = (line: string): boolean => line.trimStart().startsWith('>');

const isHorizontalRule = (line: string): boolean => {
    const t = line.trim();
    return t.length >= 3 && (/^-+$/.test(t) || /^\*+$/.test(t));
};

const isTableSeparator = (line: string | undefined): boolean => {
    const t = (line ?? '').trim();
    return t.includes('---') && /^[|:\- ]+$/.test(t);
};

const isTableStart = (line: string, next: string | undefined): boolean =>
    line.trimStart().startsWith('|') && isTableSeparator(next);

/** Does this line start a block other than a paragraph? */
const startsBlock = (line: string, next: string | undefined): boolean =>
    readHeadingLine(line) !== null
    || readListLine(line) !== null
    || isFence(line)
    || isQuote(line)
    || isHorizontalRule(line)
    || isTableStart(line, next);

/** Anchor for a heading without an explicit `{#id}`: lower-case, German
 *  umlauts transliterated, everything else collapsed to hyphens. */
export const slugifyHeading = (text: string): string =>
    text
        .toLowerCase()
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss')
        .split(/[^a-z0-9]/)
        .filter(Boolean)
        .join('-');

// ── Inline ──────────────────────────────────────────────────────────────

interface InlineMatch {
    node?: Inline;
    literal?: string;
    next: number;
}

const ESCAPABLE = '\\`*_[]()#|>-';

const readEscape = (s: string, i: number): InlineMatch | null =>
    s[i] === '\\' && i + 1 < s.length && ESCAPABLE.includes(s[i + 1]) ? { literal: s[i + 1], next: i + 2 } : null;

const readCode = (s: string, i: number): InlineMatch | null => {
    if (s[i] !== '`') return null;
    const end = s.indexOf('`', i + 1);
    return end > i ? { node: { type: 'code', text: s.slice(i + 1, end) }, next: end + 1 } : null;
};

const readStrong = (s: string, i: number): InlineMatch | null => {
    if (s[i] !== '*' || s[i + 1] !== '*') return null;
    const end = s.indexOf('**', i + 2);
    return end > i + 2 ? { node: { type: 'strong', children: parseInline(s.slice(i + 2, end)) }, next: end + 2 } : null;
};

const OPENS_AFTER = ' („"';
const CLOSES_BEFORE = ' .,;:!?)“"';

const readEm = (s: string, i: number): InlineMatch | null => {
    const marker = s[i];
    if ((marker !== '*' && marker !== '_') || s[i + 1] === marker || s[i + 1] === ' ') return null;
    // `_` only at a word boundary, so snake_case identifiers stay text.
    if (marker === '_' && i > 0 && !OPENS_AFTER.includes(s[i - 1])) return null;
    const end = s.indexOf(marker, i + 1);
    if (end <= i + 1 || s[end - 1] === ' ') return null;
    if (marker === '_' && end + 1 < s.length && !CLOSES_BEFORE.includes(s[end + 1])) return null;
    return { node: { type: 'em', children: parseInline(s.slice(i + 1, end)) }, next: end + 1 };
};

const findClosingBracket = (s: string, open: number): number => {
    let depth = 0;
    for (let i = open; i < s.length; i += 1) {
        if (s[i] === '\\') i += 1;
        else if (s[i] === '[') depth += 1;
        else if (s[i] === ']' && --depth === 0) return i;
    }
    return -1;
};

const readLink = (s: string, i: number): InlineMatch | null => {
    if (s[i] !== '[') return null;
    const close = findClosingBracket(s, i);
    if (close < 0 || s[close + 1] !== '(') return null;
    const hrefEnd = s.indexOf(')', close + 2);
    if (hrefEnd < 0) return null;
    return {
        node: { type: 'link', href: s.slice(close + 2, hrefEnd).trim(), children: parseInline(s.slice(i + 1, close)) },
        next: hrefEnd + 1,
    };
};

const INLINE_READERS = [readEscape, readCode, readStrong, readEm, readLink];

const matchInlineAt = (source: string, i: number): InlineMatch | null => {
    for (const read of INLINE_READERS) {
        const match = read(source, i);
        if (match) return match;
    }
    return null;
};

/** Parse inline Markdown into nodes. Unmatched markers stay literal text. */
export const parseInline = (source: string): Inline[] => {
    const nodes: Inline[] = [];
    let text = '';
    let i = 0;
    while (i < source.length) {
        const match = matchInlineAt(source, i);
        if (!match) {
            text += source[i];
            i += 1;
        } else if (match.literal !== undefined) {
            text += match.literal;
            i = match.next;
        } else {
            if (text) nodes.push({ type: 'text', text });
            text = '';
            if (match.node) nodes.push(match.node);
            i = match.next;
        }
    }
    if (text) nodes.push({ type: 'text', text });
    return nodes;
};

/** Plain text of an inline tree — for search and heading ids. */
export const inlineText = (nodes: Inline[]): string =>
    nodes.map((n) => (n.type === 'text' || n.type === 'code' ? n.text : inlineText(n.children))).join('');

// ── Tables ──────────────────────────────────────────────────────────────

const splitRow = (line: string): string[] => {
    let row = line.trim();
    if (row.startsWith('|')) row = row.slice(1);
    if (row.endsWith('|') && !row.endsWith('\\|')) row = row.slice(0, -1);
    const cells: string[] = [];
    let cell = '';
    let inCode = false;
    for (let i = 0; i < row.length; i += 1) {
        const ch = row[i];
        if (ch === '\\' && row[i + 1] === '|') {
            cell += '|';
            i += 1;
        } else if (ch === '|' && !inCode) {
            cells.push(cell.trim());
            cell = '';
        } else {
            if (ch === '`') inCode = !inCode;
            cell += ch;
        }
    }
    cells.push(cell.trim());
    return cells;
};

// ── Blocks ──────────────────────────────────────────────────────────────

/** A block reader: the block starting at line `i` and the index after it,
 *  or null when the line does not start this kind of block. */
type BlockReader = (lines: string[], i: number) => [Block, number] | null;

const readFence: BlockReader = (lines, i) => {
    if (!isFence(lines[i])) return null;
    let end = i + 1;
    while (end < lines.length && !isFence(lines[end])) end += 1;
    return [{ type: 'code', text: lines.slice(i + 1, end).join('\n') }, end + 1];
};

const readHeading: BlockReader = (lines, i) => {
    const heading = readHeadingLine(lines[i]);
    if (!heading) return null;
    const { text: raw, anchor } = splitAnchor(heading.rest);
    const children = parseInline(raw);
    const text = inlineText(children);
    return [{ type: 'heading', level: heading.level, id: anchor ?? slugifyHeading(text), children, text }, i + 1];
};

const readRule: BlockReader = (lines, i) => (isHorizontalRule(lines[i]) ? [{ type: 'hr' }, i + 1] : null);

const readTable: BlockReader = (lines, i) => {
    if (!isTableStart(lines[i], lines[i + 1])) return null;
    const header = splitRow(lines[i]).map(parseInline);
    const rows: Inline[][][] = [];
    let next = i + 2;
    while (next < lines.length && lines[next].trimStart().startsWith('|')) {
        rows.push(splitRow(lines[next]).map(parseInline));
        next += 1;
    }
    return [{ type: 'table', header, rows }, next];
};

const readQuote: BlockReader = (lines, i) => {
    if (!isQuote(lines[i])) return null;
    const inner: string[] = [];
    let next = i;
    while (next < lines.length && isQuote(lines[next])) {
        const body = lines[next].trimStart().slice(1);
        inner.push(body.startsWith(' ') ? body.slice(1) : body);
        next += 1;
    }
    return [{ type: 'blockquote', blocks: parseBlocks(inner) }, next];
};

/** Does the list continue after the blank line at `i`? */
const listContinuesAfterGap = (lines: string[], i: number, sameList: (line: string) => boolean, baseIndent: number): boolean => {
    let j = i + 1;
    while (j < lines.length && isBlank(lines[j])) j += 1;
    return j < lines.length && (sameList(lines[j]) || indentOf(lines[j]) > baseIndent);
};

/** One list: items at the first item's indent; anything indented deeper
 *  belongs to the current item (continuation text or a nested list); blank
 *  lines between items are allowed; an unindented line directly under item
 *  text continues it (lazy continuation). */
const readList: BlockReader = (lines, i) => {
    const first = readListLine(lines[i]);
    if (!first) return null;
    const sameList = (line: string) => {
        const item = readListLine(line);
        return item !== null && item.indent === first.indent && item.ordered === first.ordered ? item : null;
    };
    const items: string[][] = [];
    let current: string[] = [];
    let column = first.contentColumn;
    let next = i;

    /** What line `n` contributes to the current item, or null = list ends. */
    const continuation = (n: number): string | null => {
        const line = lines[n];
        if (isBlank(line)) return listContinuesAfterGap(lines, n, (l) => sameList(l) !== null, first.indent) ? '' : null;
        if (indentOf(line) > first.indent) return line.slice(Math.min(column, indentOf(line)));
        if (!isBlank(lines[n - 1]) && !startsBlock(line, lines[n + 1])) return line.trim();
        return null;
    };

    while (next < lines.length) {
        const item = sameList(lines[next]);
        if (item) {
            if (current.length) items.push(current);
            current = [item.content];
            column = item.contentColumn;
        } else {
            const text = continuation(next);
            if (text === null) break;
            current.push(text);
        }
        next += 1;
    }
    if (current.length) items.push(current);

    return [
        { type: 'list', ordered: first.ordered, start: first.start, items: items.map((itemLines) => ({ blocks: parseBlocks(itemLines) })) },
        next,
    ];
};

const readParagraph: BlockReader = (lines, i) => {
    const parts: string[] = [lines[i].trim()];
    let next = i + 1;
    while (next < lines.length && !isBlank(lines[next]) && !startsBlock(lines[next], lines[next + 1])) {
        parts.push(lines[next].trim());
        next += 1;
    }
    return [{ type: 'paragraph', children: parseInline(parts.join(' ')) }, next];
};

// Order matters: a table row starts with `|`, a list item may start with `*`.
const BLOCK_READERS: BlockReader[] = [readFence, readHeading, readRule, readTable, readQuote, readList, readParagraph];

const parseBlocks = (lines: string[]): Block[] => {
    const blocks: Block[] = [];
    let i = 0;
    while (i < lines.length) {
        if (isBlank(lines[i])) {
            i += 1;
            continue;
        }
        for (const read of BLOCK_READERS) {
            const result = read(lines, i);
            if (result) {
                blocks.push(result[0]);
                i = result[1];
                break;
            }
        }
    }
    return blocks;
};

export const parseMarkdown = (source: string): Block[] => parseBlocks(source.replace(/\r\n?/g, '\n').split('\n'));

/** Every heading of an article, in order — for search and anchors. */
export const collectHeadings = (blocks: Block[]): Array<{ level: number, id: string, text: string }> =>
    blocks.flatMap((b) => (b.type === 'heading' ? [{ level: b.level, id: b.id, text: b.text }] : []));
